// CapTableEditor — editable multi-class cap table (F1.1, F1.3, F1.6). Presentation.

import { useStore } from '../../store/selectors';
import { usd, fmtShares } from '../../format/format';
import type { ShareClassKind } from '@backsolve/shared-types';
import { useAlert } from '@ascend/alert';
import { Tooltip } from '../common/Tooltip';
import { X } from 'lucide-react';

function Th({ children, help }: { children: React.ReactNode; help?: string }) {
  return (
    <th>
      {children}
      {help && <Tooltip label={help} ariaLabel={`Help: ${children}`} />}
    </th>
  );
}

const KINDS: ShareClassKind[] = ['common', 'preferred', 'option', 'warrant', 'rsu', 'pool'];

export function CapTableEditor({ entity }: { entity: string }) {
  const cap = useStore((s) => s.draftCapTable);
  const addInstrument = useStore((s) => s.addInstrument);
  const updateInstrument = useStore((s) => s.updateInstrument);
  const removeInstrument = useStore((s) => s.removeInstrument);
  const { confirm } = useAlert();

  if (!cap) {
    return <p>No cap table selected.</p>;
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">
          {cap.entityName} — v{cap.version} ({cap.asOfDate})
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() =>
            addInstrument(entity, {
              id: `inst_${Date.now()}`,
              name: 'New Instrument',
              kind: 'common',
              sharesOutstanding: 0,
              sharesAuthorized: 0,
              issuePrice: 0,
              issueDate: null,
              liquidationMultiple: null,
              participation: null,
              participationCap: null,
              seniority: null,
              conversionRatio: null,
              strikePrice: null,
              vestingMonths: null,
              expirationDate: null,
              antiDilution: null,
              notes: '',
            })
          }
        >
          + Add Instrument
        </button>
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <Th>Name</Th>
              <Th help="Instrument class — drives which waterfall inputs (liquidation terms, strike, vesting) apply.">Class</Th>
              <Th help="Currently issued/awarded share count. Used in the allocation per-class totals and the fully-diluted sum.">
                <span className="numeric">Outstanding</span>
              </Th>
              <Th help="Maximum shares the company may issue for this class. Must be ≥ Outstanding; the unissued portion of an option pool dilutes FMV.">
                <span className="numeric">Authorized</span>
              </Th>
              <Th help="Current value of outstanding shares at the issue price (issue price × outstanding). Display only — not used by the engine.">
                <span className="numeric">Issue Price</span>
              </Th>
              <th aria-label="actions"></th>
            </tr>
          </thead>
          <tbody>
            {cap.instruments.map((inst) => (
              <tr key={inst.id}>
                <td>
                  <input
                    className="input"
                    value={inst.name}
                    aria-label={`${inst.name} name`}
                    onChange={(e) => updateInstrument(entity, inst.id, { name: e.target.value })}
                  />
                </td>
                <td>
                  <select
                    className="select"
                    value={inst.kind}
                    aria-label={`${inst.name} kind`}
                    onChange={(e) =>
                      updateInstrument(entity, inst.id, { kind: e.target.value as ShareClassKind })
                    }
                  >
                    {KINDS.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="numeric">
                  <input
                    className="input input-numeric"
                    type="number"
                    value={inst.sharesOutstanding}
                    aria-label={`${inst.name} outstanding`}
                    onChange={(e) =>
                      updateInstrument(entity, inst.id, { sharesOutstanding: Number(e.target.value) })
                    }
                  />
                  <span style={{ marginLeft: 4 }}>{fmtShares(inst.sharesOutstanding)}</span>
                </td>
                <td className="numeric">
                  <input
                    className="input input-numeric"
                    type="number"
                    value={inst.sharesAuthorized}
                    aria-label={`${inst.name} authorized`}
                    onChange={(e) =>
                      updateInstrument(entity, inst.id, { sharesAuthorized: Number(e.target.value) })
                    }
                  />
                </td>
                <td className="numeric">{usd(inst.issuePrice * inst.sharesOutstanding)}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    aria-label={`Remove ${inst.name}`}
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Remove instrument?',
                        message: `“${inst.name}” will be removed from the cap table. This cannot be undone.`,
                        confirmLabel: 'Remove',
                        danger: true,
                        type: 'danger',
                      });
                      if (ok) removeInstrument(entity, inst.id);
                    }}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
